import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { MagnifyingGlass, X, CurrencyDollar, ChartLine, CurrencyBtc, Diamond, CurrencyCircleDollar, Crown, Star } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import StockCard from '../components/StockCard';
import StockDetailModal from '../components/StockDetailModal';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import TeenVestLoader from '../components/TeenVestLoader';

const ASSET_TABS = [
  { key: 'ALL', label: 'All', icon: ChartLine },
  { key: 'Watchlist', label: '⭐ Watchlist', icon: Star },
  { key: 'Stock', label: 'Stocks', icon: CurrencyDollar },
  { key: 'ETF', label: 'ETFs', icon: ChartLine },
  { key: 'Crypto', label: 'Crypto', icon: CurrencyBtc },
  { key: 'Commodity', label: 'Commodities', icon: Diamond },
  { key: 'Currency', label: 'Currencies', icon: CurrencyCircleDollar },
  { key: 'India', label: '🇮🇳 India', icon: Crown },
];

const SORT_OPTIONS = [
  { key: 'default', label: 'Default' },
  { key: 'volume', label: '🔥 Trending' },
  { key: 'gainers', label: '📈 Top Gainers' },
  { key: 'losers', label: '📉 Top Losers' },
  { key: 'price_desc', label: '💰 Price (High → Low)' },
  { key: 'price_asc', label: 'Price (Low → High)' },
];

const applySort = (list, sortKey) => {
  const arr = [...list];
  const parsePct = (x) => parseFloat(x?.change_percent || 0) || 0;
  switch (sortKey) {
    case 'volume':
      return arr.sort((a, b) => (parseInt(b.volume) || 0) - (parseInt(a.volume) || 0));
    case 'gainers':
      return arr.sort((a, b) => parsePct(b) - parsePct(a));
    case 'losers':
      return arr.sort((a, b) => parsePct(a) - parsePct(b));
    case 'price_desc':
      return arr.sort((a, b) => (b.price || 0) - (a.price || 0));
    case 'price_asc':
      return arr.sort((a, b) => (a.price || 0) - (b.price || 0));
    default:
      return arr;
  }
};

// Batch-fetch quotes for a list of stocks using a single POST
const fetchBatchQuotes = async (stocksList) => {
  if (!stocksList.length) return stocksList;
  const symbols = stocksList.map(s => s.symbol);
  try {
    const res = await api.post('/stocks/batch-quotes', { symbols });
    const quotes = res.data.quotes || {};
    return stocksList.map(stock => ({
      ...stock,
      ...(quotes[stock.symbol] || {}),
    }));
  } catch {
    return stocksList;
  }
};

const Market = () => {
  const navigate = useNavigate();
  const { symbol } = useParams();
  const { user } = useAuth();
  const [allStocks, setAllStocks] = useState([]);
  const [displayStocks, setDisplayStocks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [activeTab, setActiveTab] = useState('ALL');
  const [sortBy, setSortBy] = useState('volume');
  const searchTimer = useRef(null);

  useEffect(() => {
    fetchStocks();
  }, []);

  useEffect(() => {
    if (symbol && allStocks.length > 0) {
      const stock = allStocks.find(s => s.symbol === symbol.toUpperCase());
      if (stock) handleStockClick(stock);
    }
  }, [symbol, allStocks]);

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setDisplayStocks((prev) => applySort(prev, newSort));
  };

  const fetchWatchlist = useCallback(async () => {
    try {
      const res = await api.get('/watchlist');
      const items = (res.data?.items || []).map((it) => ({
        symbol: it.symbol,
        name: it.name,
        asset_type: it.asset_type,
        price: it.price,
        change: it.change,
        change_percent: it.change_percent,
        volume: it.volume,
        company_name: it.name,
      }));
      return items;
    } catch {
      toast.error('Could not load watchlist');
      return [];
    }
  }, []);

  const fetchStocks = async () => {
    try {
      const response = await api.get('/stocks/list?limit=1500');
      const stocksData = response.data.stocks || [];
      setTotalCompanies(response.data.total || 0);
      setAllStocks(stocksData);

      // Show first page immediately, then batch-fetch quotes
      const firstPage = stocksData.slice(0, 40);
      setDisplayStocks(firstPage);
      setLoading(false);

      setQuotesLoading(true);
      const withQuotes = await fetchBatchQuotes(firstPage);
      setDisplayStocks(applySort(withQuotes, sortBy));
      setQuotesLoading(false);
    } catch {
      toast.error('Failed to load stocks');
      setLoading(false);
    }
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    setSearchQuery('');

    if (tab === 'Watchlist') {
      setQuotesLoading(true);
      const items = await fetchWatchlist();
      setDisplayStocks(applySort(items, sortBy));
      setQuotesLoading(false);
      return;
    }

    const base = tab === 'ALL' ? allStocks : allStocks.filter(s => s.asset_type === tab);
    const page = base.slice(0, 40);
    setDisplayStocks(page);

    setQuotesLoading(true);
    const withQuotes = await fetchBatchQuotes(page);
    setDisplayStocks(applySort(withQuotes, sortBy));
    setQuotesLoading(false);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!query.trim()) {
      handleTabChange(activeTab);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      const queryLower = query.toLowerCase();

      // Watchlist search: filter the watchlist itself, not allStocks
      if (activeTab === 'Watchlist') {
        const items = await fetchWatchlist();
        const filtered = items.filter((s) => {
          const sym = (s.symbol || '').toLowerCase();
          const name = (s.name || s.company_name || '').toLowerCase();
          return sym.includes(queryLower) || name.includes(queryLower);
        });
        setDisplayStocks(applySort(filtered, sortBy));
        return;
      }

      const base = activeTab === 'ALL' ? allStocks : allStocks.filter(s => s.asset_type === activeTab);
      const filtered = base.filter(stock => {
        const sym = (stock.symbol || '').toLowerCase();
        const name = (stock.name || stock.company_name || '').toLowerCase();
        return sym.includes(queryLower) || name.includes(queryLower);
      }).slice(0, 40);

      setDisplayStocks(filtered);
      setQuotesLoading(true);
      const withQuotes = await fetchBatchQuotes(filtered);
      setDisplayStocks(applySort(withQuotes, sortBy));
      setQuotesLoading(false);
    }, 400);
  };

  const handleStockClick = async (stock) => {
    try {
      const quote = await api.get(`/stocks/${encodeURIComponent(stock.symbol)}`);
      setSelectedStock({ ...stock, ...quote.data });
      setShowTradeModal(true);
    } catch {
      toast.error('Failed to load stock details');
    }
  };

  // Count per tab (Watchlist count is computed live when active)
  const tabCounts = {};
  ASSET_TABS.forEach(t => {
    if (t.key === 'Watchlist') {
      tabCounts[t.key] = activeTab === 'Watchlist' ? displayStocks.length : '★';
    } else {
      tabCounts[t.key] = t.key === 'ALL' ? allStocks.length : allStocks.filter(s => s.asset_type === t.key).length;
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <TeenVestLoader size={112} label="Loading market data..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen noise-bg" data-testid="market-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-4xl md:text-5xl font-unbounded font-bold text-white mb-4">Markets</h1>
          <p className="text-lg text-zinc-400 mb-6">
            Showing {displayStocks.length} of <span className="text-gold font-bold">{totalCompanies}</span> assets — Stocks, ETFs, Crypto, Commodities & Currencies
          </p>

          {/* Low-cash prompt: if user has < $500 cash, suggest game modes */}
          {(user?.balance ?? 0) < 500 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="mb-5 p-4 rounded-xl bg-gradient-to-r from-purple-500/15 to-pink-500/15 border border-purple-500/30 flex flex-wrap items-center gap-3 justify-between"
              data-testid="market-lowcash-prompt"
            >
              <p className="text-sm text-purple-100">
                💡 Running low on cash? Play <span className="font-bold text-white">Game Modes</span> to earn up to <span className="text-gold font-bold">$3,000 per scenario</span> + <span className="text-purple-300 font-bold">$50</span> from the daily quiz.
              </p>
              <button
                onClick={() => navigate('/game-modes')}
                className="px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-600 text-white font-bold text-sm hover:scale-105 transition-transform"
                data-testid="market-lowcash-cta"
              >
                Earn Cash →
              </button>
            </motion.div>
          )}

          {/* Asset Type Tabs + Sort */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex gap-2 flex-1 min-w-0 overflow-x-auto scrollbar-hide pb-1">
              {ASSET_TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.key;
                return (
                  <Button
                    key={tab.key}
                    variant={isActive ? 'default' : 'ghost'}
                    onClick={() => handleTabChange(tab.key)}
                    className={`rounded-full px-5 gap-2 whitespace-nowrap relative ${
                      isActive
                        ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold'
                        : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid={`market-tab-${tab.key.toLowerCase()}`}
                  >
                    <Icon size={18} weight={isActive ? 'fill' : 'regular'} />
                    {tab.label}
                    <span className={`text-xs ml-1 ${isActive ? 'text-black/60' : 'text-zinc-600'}`}>{tabCounts[tab.key] || 0}</span>
                  </Button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs uppercase tracking-wide text-zinc-500 hidden sm:block">Sort</span>
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="bg-black/50 border border-white/10 rounded-full px-4 py-2 text-sm text-white focus:outline-none focus:border-gold/50 cursor-pointer"
                data-testid="market-sort-select"
                aria-label="Sort market"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key} className="bg-zinc-900">{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-xl">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              type="text"
              placeholder="Search by symbol or name..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-12 pr-12 py-6 bg-black/50 border-white/10 focus:border-gold/50 text-white rounded-xl"
              data-testid="stock-search-input"
            />
            {searchQuery && (
              <button onClick={() => handleSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white" data-testid="clear-search-button">
                <X size={20} />
              </button>
            )}
          </div>
        </motion.div>

        {/* Loading indicator for quotes */}
        {quotesLoading && (
          <div className="text-center mb-4">
            <span className="text-sm text-zinc-500 animate-pulse">Fetching live prices...</span>
          </div>
        )}

        {displayStocks.length === 0 ? (
          <div className="text-center py-12">
            {activeTab === 'Watchlist' && !searchQuery ? (
              <div className="max-w-md mx-auto glass-card p-8" data-testid="watchlist-empty-state">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 mb-4">
                  <Star size={32} weight="fill" className="text-black" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Your watchlist is empty</h3>
                <p className="text-zinc-400 mb-4">Click any stock and hit the <span className="text-gold font-semibold">⭐ Watchlist</span> button to save it here.</p>
                <Button onClick={() => handleTabChange('ALL')} className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full px-6" data-testid="watchlist-empty-cta">
                  Browse All Stocks
                </Button>
              </div>
            ) : (
              <p className="text-zinc-400 text-lg">No assets found matching "{searchQuery}"</p>
            )}
          </div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayStocks.map((stock, index) => (
              <motion.div key={stock.symbol} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.03, 1) }}>
                <StockCard stock={stock} onClick={() => handleStockClick(stock)} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {showTradeModal && selectedStock && (
        <StockDetailModal
          stock={selectedStock}
          onClose={() => {
            setShowTradeModal(false);
            setSelectedStock(null);
            if (symbol) navigate('/market');
            // If user is viewing their watchlist, refresh to reflect add/remove
            if (activeTab === 'Watchlist') {
              handleTabChange('Watchlist');
            }
          }}
        />
      )}
    </div>
  );
};

export default Market;
